import { useMutation } from "@tanstack/react-query";
import { FormikHelpers } from "formik";
import { useNavigate } from "react-router";
import ApiClient from "../../api";
import { useFlashMessage } from "../../hooks/useFlashMessage";
import { components, paths } from "../../lib/api/v1";

export type UpdateDrillFormValues = {
  drillName: string;
  csvFile: File | null;
  drillImage: File | null;
  contentImagesZip: File | null;
  contentSoundsZip: File | null;
  contentPdfsZip: File | null;
};

type ResponseBody =
  paths["/api/v1/sections/multi_section_batches/import_drill_sections"]["patch"]["responses"]["202"]["content"]["application/json"];

type ErrorResponse = components["schemas"]["Error"];

const fieldNameMap: Record<string, keyof UpdateDrillFormValues> = {
  drill_name: "drillName",
  csv_file: "csvFile",
  drill_image: "drillImage",
  content_images_zip: "contentImagesZip",
  content_sounds_zip: "contentSoundsZip",
  content_pdfs_zip: "contentPdfsZip",
};

export const useUpdateDrill = (materialCode: string) => {
  const navigate = useNavigate();
  const { showErrorMessage, showSuccessMessage } = useFlashMessage();

  const { isPending, mutateAsync } = useMutation<
    ResponseBody,
    ErrorResponse,
    UpdateDrillFormValues
  >({
    mutationFn: async (params) => {
      const requestBody: Record<string, string | File> = {
        steak_material_code: materialCode,
        drill_name: params.drillName,
      };

      if (params.csvFile) {
        requestBody.csv_file = params.csvFile;
      }
      if (params.drillImage) {
        requestBody.drill_image = params.drillImage;
      }
      if (params.contentImagesZip) {
        requestBody.content_images_zip = params.contentImagesZip;
      }
      if (params.contentSoundsZip) {
        requestBody.content_sounds_zip = params.contentSoundsZip;
      }
      if (params.contentPdfsZip) {
        requestBody.content_pdfs_zip = params.contentPdfsZip;
      }

      const response = await ApiClient.sendFormData(
        "/api/v1/sections/multi_section_batches/import_drill_sections",
        "PATCH",
        requestBody,
      );

      if (response.ok) {
        return response.json();
      }

      const errorData = await response.json();
      throw errorData as ErrorResponse;
    },
    onSuccess: (data) => {
      showSuccessMessage("デジタル教材の更新を開始しました");
      navigate(
        `/multi_section_batches/${data.multi_section_batch_history_id}`,
        {
          state: { from: "update" },
        },
      );
    },
  });

  const handleSubmit = async (
    values: UpdateDrillFormValues,
    formikHelpers: FormikHelpers<UpdateDrillFormValues>,
  ) => {
    try {
      await mutateAsync(values);
    } catch (error) {
      const errorResponse = error as ErrorResponse;
      if (errorResponse.errors) {
        errorResponse.errors.forEach((err) => {
          if (err.field && err.message) {
            const formFieldName = fieldNameMap[err.field];
            if (formFieldName) {
              formikHelpers.setFieldError(formFieldName, err.message);
            }
          }
        });
      } else {
        showErrorMessage("デジタル教材の更新に失敗しました");
      }
    }
  };

  return {
    isPending,
    handleSubmit,
  };
};
